import axios from 'axios';
import {
  User,
  AuthResponse,
  PDF,
  ChatMessage,
  ChatQuestionsResponse,
  ChatResponse,
  ChatActivityUser,
  ChatActivityStats,
  BulkUploadResult,
  UserFormData,
  AnalyticsOverview,
  LoginEventsResponse,
  StaffMember,
  StaffFormData,
  StaffListResponse,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (login_identifier: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', { login_identifier, password });
    return response.data;
  },

  register: async (username: string, email: string, password: string, role?: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', { username, email, password, role });
    return response.data;
  },

  getProfile: async (): Promise<{ user: User }> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  getAllUsers: async (page = 1, limit = 20, search = ''): Promise<{ users: User[], pagination: any }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await api.get(`/auth/users?${params}`);
    return response.data;
  },

  createUser: async (data: UserFormData): Promise<{ message: string; user: User }> => {
    const response = await api.post('/auth/users', data);
    return response.data;
  },

  updateUser: async (id: number, data: Partial<UserFormData>): Promise<{ message: string; user: User }> => {
    const response = await api.put(`/auth/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/auth/users/${id}`);
    return response.data;
  },

  bulkUploadUsers: async (file: File): Promise<BulkUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/auth/users/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadSampleExcel: async (): Promise<void> => {
    const response = await api.get('/auth/users/sample-excel', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample-users.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Deferred: Safari can cancel a download whose blob URL is revoked
    // before it starts
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },
};

/**
 * Team / staff directory — superadmin only.
 * Only ever lists and mutates accounts whose role is 'faculty' or 'superadmin';
 * students go through authAPI's /auth/users endpoints.
 */
export const staffAPI = {
  getStaff: async (
    page = 1,
    limit = 20,
    search = '',
    role = ''
  ): Promise<StaffListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    if (role) {
      params.append('role', role);
    }
    const response = await api.get(`/auth/staff?${params}`);
    return response.data;
  },

  createStaff: async (data: StaffFormData): Promise<{ message: string; user: StaffMember }> => {
    const response = await api.post('/auth/staff', data);
    return response.data;
  },

  updateStaff: async (
    id: number,
    data: Partial<StaffFormData>
  ): Promise<{ message: string; user: StaffMember }> => {
    const response = await api.put(`/auth/staff/${id}`, data);
    return response.data;
  },

  deleteStaff: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/auth/staff/${id}`);
    return response.data;
  },
};

export const pdfAPI = {
  upload: async (file: File): Promise<{ message: string; pdf: PDF }> => {
    const formData = new FormData();
    formData.append('pdf', file);
    
    const response = await api.post('/pdf/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAll: async (): Promise<{ pdfs: PDF[] }> => {
    const response = await api.get('/pdf');
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string; pdf: PDF }> => {
    const response = await api.delete(`/pdf/${id}`);
    return response.data;
  },

  view: (id: number): string => {
    return `${API_BASE_URL}/pdf/${id}/view`;
  },
};

export const chatAPI = {
  ask: async (question: string): Promise<ChatResponse> => {
    const response = await api.post('/chat/ask', { question });
    return response.data;
  },

  /** The user's own questions, newest first — no answers, for the history list */
  getQuestions: async (page = 1, limit = 30, search = ''): Promise<ChatQuestionsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await api.get(`/chat/questions?${params}`);
    return response.data;
  },

  /** One saved exchange, fetched when a question in the history is opened */
  getHistoryItem: async (id: number): Promise<{ message: ChatMessage }> => {
    const response = await api.get(`/chat/history/${id}`);
    return response.data;
  },

  getAdminActivity: async (
    page = 1,
    limit = 20,
    search = ''
  ): Promise<{ users: ChatActivityUser[]; stats: ChatActivityStats; pagination: any }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await api.get(`/chat/admin/activity?${params}`);
    return response.data;
  },

  getAdminUserHistory: async (
    userId: number,
    page = 1,
    limit = 20
  ): Promise<{
    user: { id: number; username: string; email: string; phone_number?: string };
    history: ChatMessage[];
    pagination: any;
  }> => {
    const response = await api.get(`/chat/admin/history/${userId}?page=${page}&limit=${limit}`);
    return response.data;
  },
};

export const analyticsAPI = {
  getOverview: async (days = 30): Promise<AnalyticsOverview> => {
    const params = new URLSearchParams({
      days: days.toString(),
    });
    const response = await api.get(`/analytics/overview?${params}`);
    return response.data;
  },

  getLogins: async (page = 1, limit = 20, search = ''): Promise<LoginEventsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) {
      params.append('search', search);
    }
    const response = await api.get(`/analytics/logins?${params}`);
    return response.data;
  },
};

export default api;
